'use client'

import { useEffect } from 'react'
import type { PluginListenerHandle } from '@capacitor/core'
import { isNativePlatform } from '@/lib/utils/platform'
import {
  attachNativePushListeners,
  onPushTapped,
  clearDeliveredNotifications,
} from '@/lib/utils/push-notifications'
import { getNotificationDeepLink } from '@/lib/notifications/deep-link'

const PENDING_DEEPLINK_KEY = 'push_pending_deeplink'

/**
 * Earliest-mounted client component for push wiring.
 *
 * Capacitor fires `pushNotificationActionPerformed` very early on cold launch
 * — before auth or `/in/[orgSlug]` mounts. If we wait until the dashboard
 * layout to attach the listener, the tap is dropped and the user lands on
 * the home screen instead of the conversation/post they tapped.
 *
 * This component runs from the root layout. It:
 *  1. Attaches the native listeners as soon as JS hydrates.
 *  2. Subscribes to taps and persists the resulting deep link to
 *     `@capacitor/preferences`. The authenticated layout's
 *     `usePushNotifications` hook drains and navigates to it on mount.
 *  3. Clears delivered tray notifications so the badge stays in sync.
 *
 * Renders nothing.
 */
export function PushBootstrap() {
  useEffect(() => {
    if (!isNativePlatform()) return

    let cancelled = false
    let resumeHandle: PluginListenerHandle | null = null
    let unsubTap: (() => void) | null = null

    async function bootstrap() {
      await attachNativePushListeners()
      if (cancelled) return

      // Drain delivered notifications on first run so the tray reflects
      // current unread state, not whatever the user already read on web.
      clearDeliveredNotifications().catch(() => {})

      // Persist tap deep links so the authenticated layout can pick them
      // up regardless of when it mounts (cold launch can fire this listener
      // before React routes to /in/[orgSlug]).
      unsubTap = onPushTapped((data) => {
        const orgSlug = (data.orgSlug as string) || ''
        const url = getNotificationDeepLink((data.type as string) || '', data, orgSlug)
        if (!url) return

        // Fire-and-forget storage write — auth layer drains it on mount.
        import('@capacitor/preferences')
          .then(({ Preferences }) =>
            Preferences.set({
              key: PENDING_DEEPLINK_KEY,
              value: JSON.stringify({ url, ts: Date.now() }),
            })
          )
          .catch(() => {})
      })

      // Clear the system tray every time the user brings the app back to the
      // foreground. Anything still in the tray was either already read on
      // web or is about to be read here, so keeping it around just makes the
      // badge wrong.
      try {
        const { App } = await import('@capacitor/app')
        resumeHandle = await App.addListener('resume', () => {
          clearDeliveredNotifications().catch(() => {})
        })
      } catch {
        /* @capacitor/app missing — skip resume handling */
      }
    }

    bootstrap().catch(() => {})

    return () => {
      cancelled = true
      if (unsubTap) {
        unsubTap()
        unsubTap = null
      }
      if (resumeHandle) {
        resumeHandle.remove().catch(() => {})
        resumeHandle = null
      }
    }
  }, [])

  return null
}
